// Hand-rolled SVG/CSS charts for the Stats page (phase 16). No charting
// dependency — bundle size, free tier and the quiet brand all argue against
// one. Mono axis labels, gold = money, teal = counts, faint gridlines.

import type { ReactNode } from 'react';
import { useIsMobile } from '../lib/useIsMobile';
import { scaleMonthBars } from '../lib/statsMobile';

// ── Grouped month bars (revenue gold / expenses faint) ───────────────────

export interface MonthBar {
  label: string;
  /** Gold series (revenue). */
  primary: number;
  /** Dim series (expenses). */
  secondary: number;
  /** Hover titles. */
  primaryTitle?: string;
  secondaryTitle?: string;
  current?: boolean;
  /** Renders the primary bar faint instead of gold — EOFY wrapped's
      "busiest month" chart dims every month but the peak. */
  dim?: boolean;
}

const W = 760;
const H = 196;
const PLOT_TOP = 12;
const PLOT_BOTTOM = H - 26; // x labels live below

export function MonthBars({ data, formatAxis }: { data: MonthBar[]; formatAxis: (n: number) => string }) {
  const isMobile = useIsMobile();

  // ≤880px (mobile overhaul Phase D §11.1 B): the fixed 760px viewBox would
  // squeeze twelve pairs of bars into 353px, so the chart becomes a flex row
  // of percentage-height columns — one per month, revenue stacked on the grey
  // expense plinth. No gridlines or y-axis: the vitals below the chart carry
  // the numbers at this width, which is what `formatAxis` was for.
  if (isMobile) return <MonthBarsMobile data={data} />;

  const max = Math.max(1, ...data.map((d) => Math.max(d.primary, d.secondary)));
  const slot = W / Math.max(1, data.length);
  const barW = Math.min(20, slot / 3);
  const y = (v: number) => PLOT_BOTTOM - (v / max) * (PLOT_BOTTOM - PLOT_TOP);
  const gridLevels = [0.25, 0.5, 0.75, 1];

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" role="img">
      {gridLevels.map((g) => (
        <g key={g}>
          <line className="chart-grid" x1={0} x2={W} y1={y(max * g)} y2={y(max * g)} />
          <text className="chart-axis" x={2} y={y(max * g) - 3}>
            {formatAxis(max * g)}
          </text>
        </g>
      ))}
      <line className="chart-grid base" x1={0} x2={W} y1={PLOT_BOTTOM} y2={PLOT_BOTTOM} />
      {data.map((d, i) => {
        const cx = slot * i + slot / 2;
        return (
          <g key={d.label + i}>
            <rect
              className={`chart-bar ${d.dim ? 'dim' : 'gold'}`}
              x={cx - barW - 1.5}
              width={barW}
              y={y(d.primary)}
              height={Math.max(d.primary > 0 ? 1.5 : 0, PLOT_BOTTOM - y(d.primary))}
              rx={1.5}
            >
              {d.primaryTitle && <title>{d.primaryTitle}</title>}
            </rect>
            <rect
              className="chart-bar dim"
              x={cx + 1.5}
              width={barW}
              y={y(d.secondary)}
              height={Math.max(d.secondary > 0 ? 1.5 : 0, PLOT_BOTTOM - y(d.secondary))}
              rx={1.5}
            >
              {d.secondaryTitle && <title>{d.secondaryTitle}</title>}
            </rect>
            <text className={`chart-axis x${d.current ? ' current' : ''}`} x={cx} y={H - 8} textAnchor="middle">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** The ≤880px render of MonthBars (mockup §11.1 B, `.bars` / `.bars-x`). The
    heights come from `scaleMonthBars`, which scales to the tallest stack so a
    column can never spill out of its 120px box. The month labels sit in their
    own row under the bars on the same flex geometry, so label and column stay
    aligned without a viewBox to anchor them. */
function MonthBarsMobile({ data }: { data: MonthBar[] }) {
  const heights = scaleMonthBars(data);
  return (
    <>
      <div className="mbars">
        {data.map((d, i) => (
          <div className={`b${d.dim ? ' dim' : ''}`} key={d.label + i}>
            {heights[i].primary > 0 && (
              <i
                className={heights[i].secondary > 0 ? 'split' : undefined}
                style={{ height: `${heights[i].primary}%` }}
                title={d.primaryTitle}
              />
            )}
            {heights[i].secondary > 0 && (
              <i className="x" style={{ height: `${heights[i].secondary}%` }} title={d.secondaryTitle} />
            )}
          </div>
        ))}
      </div>
      <div className="mbars-x">
        {data.map((d, i) => (
          <span className={d.current ? 'cur' : undefined} key={d.label + i}>
            {d.label}
          </span>
        ))}
      </div>
    </>
  );
}

// ── Horizontal bar list (pipeline, project types) — HTML, crisper ────────

export interface HBarItem {
  label: string;
  value: number;
  /** Right-hand display; defaults to the raw value. */
  display?: ReactNode;
}

export function HBars({ items, tone = 'accent' }: { items: HBarItem[]; tone?: 'accent' | 'gold' }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="hbars">
      {items.map((item, i) => (
        <div className="hbar-row" key={i}>
          <span className="hbar-label" title={item.label}>{item.label}</span>
          <span className="hbar-track">
            <span className={`hbar-fill ${tone}`} style={{ width: `${(item.value / max) * 100}%` }} />
          </span>
          <span className="hbar-value">{item.display ?? item.value}</span>
        </div>
      ))}
    </div>
  );
}
